import { type Ref, ref } from "vue";

import { useQuery } from "@tanstack/vue-query";
import { useMutation } from "@tanstack/vue-query";

import { FIVE_MINUTES } from "@/helpers/constants/time";
import { useAuth } from "@/hooks/auth";
import { queryClient } from "@/query/queryClient";
import accessTokensService from "@/services/modules/accessTokens";
import optionsApiService from "@/services/modules/options";

import type { IPaginatedResponse } from "@/models/common";
import type { AccessToken } from "@/schemas/accessTokens";
import type {
  CreateAccessTokenPayload,
  GetAccessTokensParams,
  UpdateAccessTokenPayload,
} from "@/services/modules/accessTokens";

const ACCESS_TOKENS_QUERY_KEYS = {
  LIST: "access-tokens-list",
  OPTIONS: "access-tokens-options",
};

export const useAccessTokensQuery = (
  params: Ref<GetAccessTokensParams> = ref({})
) => {
  const { currentUser } = useAuth();

  return useQuery({
    queryKey: [ACCESS_TOKENS_QUERY_KEYS.LIST, () => currentUser?.id, params],
    queryFn: async () => {
      return accessTokensService.list(params.value);
    },
    staleTime: FIVE_MINUTES,
    enabled: !!currentUser?.id,
  });
};

export const useAccessTokenDeleteMutation = () => {
  return useMutation({
    mutationFn: async (tokenId: AccessToken["id"]) => {
      return accessTokensService.delete(tokenId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ACCESS_TOKENS_QUERY_KEYS.LIST],
      });
    },
  });
};

export const useAccessTokenUpdateMutation = () => {
  return useMutation({
    mutationFn: async ({
      tokenId,
      data,
    }: {
      tokenId: AccessToken["id"];
      data: UpdateAccessTokenPayload;
    }) => {
      return accessTokensService.update({ tokenId, data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ACCESS_TOKENS_QUERY_KEYS.LIST],
      });
    },
  });
};

export const useAccessTokensOptionsQuery = () => {
  return useQuery({
    queryKey: [ACCESS_TOKENS_QUERY_KEYS.OPTIONS],
    queryFn: async () => {
      return optionsApiService.getAccessTokensOptions();
    },
    staleTime: Infinity,
  });
};

export const useAccessTokenCreateMutation = () => {
  return useMutation({
    mutationFn: async (data: CreateAccessTokenPayload) => {
      return accessTokensService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ACCESS_TOKENS_QUERY_KEYS.LIST],
      });
    },
  });
};

export const getAccessTokenFromCache = (
  tokenId: AccessToken["id"]
): AccessToken | undefined => {
  const queries = queryClient.getQueriesData<IPaginatedResponse<AccessToken>>({
    queryKey: [ACCESS_TOKENS_QUERY_KEYS.LIST],
  });

  for (const [, data] of queries) {
    const token = data?.data.find((t: AccessToken) => t.id === tokenId);
    if (token) {
      return token;
    }
  }
};
